import moment from 'moment';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { TaskDashboardItem } from '@frontend/api';
import {
  Box,
  Button,
  CalendarIcon,
  Flex,
  HStack,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  Text,
  UserGroupBadge,
  VStack,
} from '@frontend/legacy-components';
import { useDateFormat } from '@frontend/utils';

import { InteractiveFlex } from 'components/lib2/InteractiveContainers';
import MentionsDisplay from 'components/lib2/MentionsDisplay';

import { TASK_STATUSES } from 'modules/tasks/data/constants';

import { getGroupPrimaryKey } from 'store/session/selectors';

import { toMoment } from 'utils/dateUtils';
import { useUserGroupBadgeLookup } from 'utils/multisite/hooks/useUserGroupBadge';

import ActionSwitchGroupPopover from './ActionSwitchGroupPopover';

type TaskDashboardActionProps = {
  task: TaskDashboardItem;
};

export const TaskDashboardAction = ({ task }: TaskDashboardActionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [quoteLinkPopoverIndex, setQuoteLinkPopoverIndex] = useState<
    number | null
  >(null);

  const groupId = useSelector(getGroupPrimaryKey);
  const getUserGroupBadgeProps = useUserGroupBadgeLookup();
  const badgeProps = getUserGroupBadgeProps(task.groupId);

  const { dateAtTimeFormat } = useDateFormat();

  const taskLink = useMemo(() => {
    if (task.annotation?.commentedOnQuoteItemId) {
      return `/quotes/edit/${task.annotation.commentedOnQuoteNumberWithRevision}?quote_item=${task.annotation.commentedOnQuoteItemId}`;
    }
    if (task.part) {
      return `/parts/viewer/${task.part.uuid}?annotation_id=${
        task.annotation?.id ?? ''
      }`;
    }
    return null;
  }, [task]);

  const isCurrentGroup = groupId === task.groupId;
  const hasRelatedQuotes = task.quoteItemComponents.length > 0;
  const taskMessage = task.annotation?.comments?.[0]?.content ?? null;

  const isOverdue =
    task.status !== TASK_STATUSES.RESOLVED &&
    !!task.dueDate &&
    toMoment(task.dueDate) < moment();

  const handleQuoteLinkClick = (e: React.MouseEvent, index: number) => {
    if (!isCurrentGroup) {
      e.preventDefault();
      e.stopPropagation();
      setQuoteLinkPopoverIndex(index);
    }
  };

  const relatedQuoteLinks = task.quoteItemComponents.map((qic, index) => {
    const url = `/quotes/edit/${qic.quoteNumberWithRevision}?quote_item=${qic.quoteItemId}`;
    const label =
      index === 0
        ? `Relates to Quote${task.quoteItemComponents.length > 1 ? 's' : ''} #${
            qic.quoteNumberWithRevision
          }`
        : `#${qic.quoteNumberWithRevision}`;

    if (isCurrentGroup) {
      return (
        <Button
          key={qic.quoteItemId}
          as={Link}
          variant="link"
          size="sm"
          to={url}
          flexShrink={0}
        >
          {label}
        </Button>
      );
    }

    return (
      <ActionSwitchGroupPopover
        key={qic.quoteItemId}
        toggleOpen={(open) => {
          const next =
            typeof open === 'function'
              ? open(quoteLinkPopoverIndex === index)
              : open;
          setQuoteLinkPopoverIndex(next ? index : null);
        }}
        isOpen={quoteLinkPopoverIndex === index}
        nextUrl={url}
        groupId={task.groupId}
      >
        <Button
          variant="link"
          size="sm"
          onClick={(e: React.MouseEvent) => handleQuoteLinkClick(e, index)}
          flexShrink={0}
        >
          {label}
        </Button>
      </ActionSwitchGroupPopover>
    );
  });

  const cardContent = (
    <VStack align="stretch" gap={1} width="100%">
      <HStack gap={1} align="center" width="100%">
        <HStack
          flex="1 1 0"
          minWidth={0}
          gap={1}
          align="center"
          justify="flex-start"
        >
          {task.part?.partNumber && (
            <Part
              number={task.part.partNumber}
              filename={task.part.filename}
              rev={task.part.revision}
              size="xl"
              variant="borderless"
              minWidth={0}
            >
              <PartLabel
                display="flex"
                alignItems="baseline"
                minWidth={0}
                maxWidth="100%"
                sx={{
                  '> .part__label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    wordBreak: 'normal',
                  },
                  '.part__label-group': {
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Part>
          )}
          {task.part?.filename && (
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="feature.secondary"
              flexShrink={1}
              minWidth={0}
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
            >
              {task.part.filename}
            </Text>
          )}
        </HStack>
        {badgeProps && (
          <Box
            flex="0 1 auto"
            maxWidth="50%"
            minWidth={0}
            display="flex"
            justifyContent="flex-end"
          >
            <UserGroupBadge
              maxWidth="100%"
              size="sm"
              name={badgeProps.name}
              colorPalette={badgeProps.colorPalette}
              icon={badgeProps.icon}
            />
          </Box>
        )}
      </HStack>

      <HStack gap={1.5} align="flex-start" width="100%">
        <VStack flex={1} minWidth={0} align="start" gap={0.5}>
          {taskMessage && (
            <Text fontSize="md" lineHeight="short" noOfLines={3}>
              &ldquo;
              <MentionsDisplay content={taskMessage} display="inline" />
              &rdquo;
            </Text>
          )}

          {hasRelatedQuotes && (
            <Flex gap={0.5} align="center" flexWrap="wrap">
              {task.quoteItemComponents.map((qic, i) => (
                <Flex key={qic.quoteItemId} align="center" gap={0.5}>
                  {relatedQuoteLinks[i]}
                  {i < relatedQuoteLinks.length - 1 && (
                    <Text size="xs" color="feature.secondary">
                      ,
                    </Text>
                  )}
                </Flex>
              ))}
            </Flex>
          )}
        </VStack>

        {task.part && (
          <Part
            number={task.part.partNumber}
            filename={task.part.filename}
            rev={task.part.revision}
            size="xl"
            variant="borderless"
            alignSelf="flex-start"
          >
            <PartThumbnailContainer>
              <PartThumbnail
                partFileUuid={task.part.primaryFileUuid}
                containsVirus={task.part.containsVirus}
                filename={task.part.filename}
              />
            </PartThumbnailContainer>
          </Part>
        )}
      </HStack>

      <HStack gap={1} align="center" width="100%">
        <Text
          size="sm"
          fontWeight="medium"
          color="feature.secondary"
          flex="1 1 0"
          minWidth={0}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          Assigned by {task.createdByFullName}
        </Text>

        {task.dueDate && (
          <HStack
            gap={0.5}
            align="center"
            flexShrink={0}
            color={isOverdue ? 'interaction.destructive' : 'feature.secondary'}
          >
            <CalendarIcon
              boxSize={1.75}
              color={
                isOverdue ? 'interaction.destructive' : 'feature.secondary'
              }
            />
            <Text
              size="sm"
              fontWeight="medium"
              whiteSpace="nowrap"
              color={
                isOverdue ? 'interaction.destructive' : 'feature.secondary'
              }
            >
              {isOverdue
                ? `Overdue since ${toMoment(task.dueDate).format(
                    dateAtTimeFormat,
                  )}`
                : `Due ${toMoment(task.dueDate).format(dateAtTimeFormat)}`}
            </Text>
          </HStack>
        )}
      </HStack>
    </VStack>
  );

  if (!taskLink) {
    return (
      <Box
        display="block"
        width="100%"
        p={1.5}
        backgroundColor="page.surface"
        color="feature.primary"
        borderBottom="1px solid"
        borderColor="separator.divider"
      >
        {cardContent}
      </Box>
    );
  }

  if (isCurrentGroup) {
    return (
      <Box
        as={Link}
        to={taskLink}
        display="block"
        width="100%"
        p={1.5}
        backgroundColor="page.surface"
        color="feature.primary"
        borderBottom="1px solid"
        borderColor="separator.divider"
        _hover={{
          color: 'feature.primary',
          backgroundColor: 'interaction.surfaceHover',
        }}
        _active={{
          color: 'feature.primary',
          backgroundColor: 'interaction.surfaceActive',
        }}
      >
        {cardContent}
      </Box>
    );
  }

  return (
    <ActionSwitchGroupPopover
      toggleOpen={setIsOpen}
      isOpen={isOpen}
      nextUrl={taskLink}
      groupId={task.groupId}
    >
      <InteractiveFlex
        onClick={() => setIsOpen(true)}
        width="100%"
        p={1.5}
        color="feature.primary"
        borderBottom="1px solid"
        borderColor="separator.divider"
      >
        {cardContent}
      </InteractiveFlex>
    </ActionSwitchGroupPopover>
  );
};
